import {
  AlertTriangle, CalendarDays, Check, Download, ExternalLink, MessageCircle,
  Play, Quote, Send,
} from "lucide-react";
import { useEffect, useState } from "react";
import { API_URL } from "@/lib/api";
import { isAppearanceVisible, timerState } from "@/lib/lessonVisibility";

const iconMap = {
  external: ExternalLink,
  whatsapp: MessageCircle,
  telegram: Send,
  download: Download,
  calendar: CalendarDays,
  play: Play,
};

function TimerBlock({ block, context, timestamp }) {
  const state = timerState({
    mode: block.timer_mode || "lesson_open",
    duration_seconds: block.duration_seconds,
    end_at: block.end_at,
    on_expire: block.on_expire || "stay",
  }, context, timestamp);
  if (!state.ready || (state.expired && block.on_expire === "hide")) return null;
  const labels = ["DNI", "GODZ.", "MIN.", "SEK."];
  return <section className={`lesson-timer ${state.expired ? "expired" : ""}`} data-testid={`lesson-timer-${block.id}`}>
    <small>LIMIT CZASOWY</small><h3>{state.expired ? (block.expired_text || "Czas minął") : (block.title || "Pozostały czas")}</h3>
    <div>{state.parts.map((part, index) => <span key={labels[index]}><b>{String(part).padStart(2, "0")}</b><em>{labels[index]}</em></span>)}</div>
  </section>;
}

function ActionIcon({ name }) {
  const Icon = iconMap[name];
  return Icon ? <Icon aria-hidden="true"/> : null;
}

export const LessonBlockRenderer = ({ blocks = [], preview = false, onButtonClick, context = {} }) => {
  const [timestamp, setTimestamp] = useState(() => Date.now());
  const hasTimedContent = blocks.some((block) => block.type === "timer" || block.appearance?.mode === "lesson_open" || block.appearance?.mode === "date");
  useEffect(() => {
    if (!hasTimedContent) return undefined;
    const timer = window.setInterval(() => setTimestamp(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasTimedContent]);
  const mediaUrl = (value) => value?.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${value}?auth=${localStorage.getItem("marsem_token")}` : value;

  return <div className={`lesson-blocks ${preview ? "preview" : ""}`} data-testid={preview ? "lesson-block-preview" : "lesson-block-content"}>
    {blocks.map((block) => {
      if (!preview && !isAppearanceVisible(block.appearance, context, timestamp)) return null;
      if (block.type === "heading") return <h2 key={block.id}>{block.text}</h2>;
      if (block.type === "text") return <p key={block.id}>{block.text}</p>;
      if (block.type === "image") return <figure key={block.id}><img src={mediaUrl(block.url)} alt={block.alt || ""}/>{block.caption ? <figcaption>{block.caption}</figcaption> : null}</figure>;
      if (block.type === "video") return <div className="block-video" key={block.id}><iframe src={mediaUrl(block.url)} title={block.title || "Video"} allowFullScreen/></div>;
      if (block.type === "quote") return <blockquote key={block.id}><Quote/>{block.text}<cite>{block.author}</cite></blockquote>;
      if (block.type === "alert") return <div className="block-alert" key={block.id}><AlertTriangle/><span>{block.text}</span></div>;
      if (block.type === "divider") return <hr key={block.id}/>;
      if (block.type === "checklist") return <ul className="block-checklist" key={block.id}>{(block.items || []).map((item, index) => <li key={index}><Check/>{item}</li>)}</ul>;
      if (block.type === "columns") return <div className="block-columns" key={block.id}><div>{block.left}</div><div>{block.right}</div></div>;
      if (block.type === "timer") return <TimerBlock block={block} context={context} timestamp={timestamp} key={block.id}/>;
      if (block.type === "button") return <a className={`block-button style-${block.style || "gold"} size-${block.size || "medium"}`} href={block.url} target={block.new_tab === false ? "_self" : "_blank"} rel="noreferrer" key={block.id} onClick={() => onButtonClick?.(block.id, block.label)}><ActionIcon name={block.icon || "external"}/><span>{block.label}</span></a>;
      if (block.type === "file") {
        const href = block.file_id ? `${API_URL}/files/${block.file_id}?auth=${localStorage.getItem("marsem_token")}` : block.url;
        return <a className="block-file" href={href} key={block.id} onClick={() => onButtonClick?.(block.id, block.label || "Pobierz materiał")}><Download/><span><strong>{block.label || "Pobierz materiał"}</strong><small>{block.description}</small></span></a>;
      }
      return null;
    })}
  </div>;
};
